import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { Spin } from 'antd';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import axios from '../Common/axios';
import { getDashboardHistory } from '../Common/api';
import { chart_options_bar } from '../Common/options';

// 로드 순서와 무관하게 막대그래프가 동작하도록 명시적 등록(중복 등록은 무해)
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// 평균선(점선) 플러그인 - 막대 점수들의 평균 위치에 가로 점선 표시
const avgLinePlugin = {
    id: 'avgLine',
    afterDatasetsDraw(chart) {
        const ds = chart.data.datasets && chart.data.datasets[0];
        if (!ds || !ds.data || ds.data.length === 0) return;

        const values = ds.data.map((v) => Number(v) || 0);
        const avg = values.reduce((a, b) => a + b, 0) / values.length;

        const { ctx, chartArea, scales } = chart;
        if (!scales || !scales.y) return;
        const y = scales.y.getPixelForValue(avg);

        ctx.save();
        // 점선
        ctx.beginPath();
        ctx.setLineDash([6, 5]);
        ctx.lineWidth = 2;
        ctx.strokeStyle = 'rgba(60, 65, 74, 0.9)';
        ctx.moveTo(chartArea.left, y);
        ctx.lineTo(chartArea.right, y);
        ctx.stroke();
        // 라벨
        ctx.setLineDash([]);
        ctx.fillStyle = 'rgba(60, 65, 74, 1)';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'right';
        ctx.textBaseline = 'bottom';
        ctx.fillText(`평균 ${avg.toFixed(1)}`, chartArea.right - 4, y - 3);
        ctx.restore();
    },
};

// 파스텔톤 10색 (인접 색이 비슷하지 않도록 색상환을 크게 건너뛰며 배치)
// 순서대로 적용하고 11번째부터 다시 처음 색으로 순환(index % 10)
const PASTEL = [
    'rgba(245, 183, 177, 0.9)', // 1 코랄 레드
    'rgba(174, 198, 235, 0.9)', // 2 블루
    'rgba(171, 214, 170, 0.9)', // 3 그린
    'rgba(210, 190, 230, 0.9)', // 4 퍼플
    'rgba(247, 208, 165, 0.9)', // 5 오렌지
    'rgba(160, 214, 205, 0.9)', // 6 틸
    'rgba(244, 187, 214, 0.9)', // 7 핑크
    'rgba(240, 226, 165, 0.9)', // 8 옐로우
    'rgba(184, 186, 228, 0.9)', // 9 인디고
    'rgba(203, 224, 168, 0.9)', // 10 라임
];
const PASTEL_BORDER = [
    'rgb(226, 141, 133)',
    'rgb(133, 168, 219)',
    'rgb(126, 186, 124)',
    'rgb(168, 137, 200)',
    'rgb(230, 170, 110)',
    'rgb(110, 186, 174)',
    'rgb(224, 140, 178)',
    'rgb(214, 194, 110)',
    'rgb(138, 141, 205)',
    'rgb(160, 194, 110)',
];

/**
 * 선택 회사의 자산그룹별 보안점수 막대그래프 (x: 자산그룹, y: 점수)
 * props:
 *   reportList  : 자산그룹명 매핑용 (asset_type -> asset_type_nm)
 *   companyCode : 선택된 회사코드
 */
function GroupScoreChart({ reportList, companyCode }) {
    const [labels, setLabels] = useState([]);
    const [scores, setScores] = useState([]);
    const [spinflag, setSpinflag] = useState(true);

    useEffect(() => {
        if (companyCode) {
            fetchScores();
        }
    }, [companyCode]);

    const fetchScores = () => {
        setSpinflag(true);

        // 자산구분코드 -> 자산그룹명 매핑
        const nameMap = {};
        (reportList || []).forEach((r) => {
            if (r.asset_type) nameMap[r.asset_type] = r.asset_type_nm || r.asset_type;
        });

        axios.get(getDashboardHistory(companyCode))
            .then((response) => {
                const data = response.data || {};
                const lbls = [];
                const scrs = [];
                // data: { assetTypeCode: [ {check_date, avg_level, ...}, ... ] } (check_date 내림차순)
                Object.entries(data).forEach(([assetType, items]) => {
                    if (Array.isArray(items) && items.length > 0) {
                        lbls.push(nameMap[assetType] || assetType);
                        scrs.push(Number(items[0].avg_level) || 0);
                    }
                });
                setLabels(lbls);
                setScores(scrs);
            })
            .catch((error) => {
                console.error('GroupScoreChart error', error);
                setLabels([]);
                setScores([]);
            })
            .finally(() => setSpinflag(false));
    };

    const data = {
        labels,
        datasets: [
            {
                label: '보안점수',
                data: scores,
                backgroundColor: scores.map((_, i) => PASTEL[i % PASTEL.length]),
                borderColor: scores.map((_, i) => PASTEL_BORDER[i % PASTEL_BORDER.length]),
                borderWidth: 1,
                categoryPercentage: 0.6,
                barPercentage: 0.8,
            },
        ],
    };

    return (
        <Spin size="large" tip="Loading..." spinning={spinflag}>
            <div className="section" style={{ padding: '5px' }}>
                <div className="sub_title">{'■ 자산그룹별 보안점수'}</div>
                <div style={{ padding: '5px 10px 12px', height: '340px' }}>
                    {labels.length > 0 ? (
                        <Bar options={chart_options_bar} data={data} plugins={[avgLinePlugin]} />
                    ) : (
                        <div style={{ padding: '20px', color: '#999' }}>
                            표시할 보안점수 데이터가 없습니다.
                        </div>
                    )}
                </div>
            </div>
        </Spin>
    );
}

export default GroupScoreChart;
